import {
  CheckCircleIcon,
  CloseIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from "@/icons/primary";
import classNames from "classnames";
import React, { useState } from "react";

export type CaAlertProps = {
  intent?: "success" | "info" | "warning" | "error";
  message?: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  closable?: boolean;
  onClose?: () => void;
  className?: string;
};

const INTENT_ICONS: Record<string, any> = {
  success: CheckCircleIcon,
  info: InformationCircleIcon,
  warning: ExclamationTriangleIcon,
  error: XCircleIcon,
};

const INTENT_CLASSES: Record<string, any> = {
  success: {
    card: "!bg-green-50",
    icon: "!text-green-400",
    message: "!text-green-800",
    description: "!text-green-700",
  },
  info: {
    card: "!bg-blue-50",
    icon: "!text-blue-700",
    message: "!text-blue-800",
    description: "!text-blue-700",
  },
  warning: {
    card: "!bg-yellow-50",
    icon: "!text-yellow-400",
    message: "!text-yellow-800",
    description: "!text-yellow-700",
  },
  error: {
    card: "!bg-red-50",
    icon: "!text-red-400",
    message: "!text-red-800",
    description: "!text-red-700",
  },
};

function CaAlert({
  intent,
  children,
  message,
  actions,
  closable,
  onClose,
  className,
}: CaAlertProps) {
  const [isClosed, setIsClosed] = useState(false);
  const selectedIntentClasses = intent ? INTENT_CLASSES[intent] : INTENT_CLASSES["success"];
  const Icon = intent ? INTENT_ICONS[intent] : INTENT_ICONS["success"];

  const handleClose = () => {
    setIsClosed(true);
    onClose && onClose();
  };

  if (isClosed) {
    return null;
  }

  return (
    <div
      className={classNames(
        "flex items-center justify-between rounded-base p-4",
        selectedIntentClasses.card,
        className,
      )}
    >
      <div className='flex justify-start items-start space-x-3'>
        <div className='h-full'>
          <Icon className={`text-2xl flex ${selectedIntentClasses.icon}`} />
        </div>
        <div className='flex flex-col'>
          {message && (
            <div className={`font-medium text-base ${selectedIntentClasses.message}`}>
              {message}
            </div>
          )}
          {children && (
            <div className={`font-normal mt-2 text-sm ${selectedIntentClasses.description}`}>
              {children}
            </div>
          )}
          {actions && (
            <div className='mt-4 flex space-x-2.5 items-center justify-start'>{actions}</div>
          )}
        </div>
      </div>
      {closable && (
        <button
          type='button'
          className={`text-gray-400 ml-4 p-1 rounded-full ${selectedIntentClasses.message} self-start`}
          onClick={handleClose}
        >
          <CloseIcon className='text-xl flex' />
        </button>
      )}
    </div>
  );
}

CaAlert.defaultProps = {
  intent: "success",
  closable: true,
  actions: null,
  onClose: () => {},
  message: "",
  children: "",
};

export default CaAlert;
